// File: /js/shared/quiz/kprime-slider.js
// Role: participant-side tri-state Vrai/Faux slider for Kprime questions
// (answer_format='kprime'). Design §5.3.
//
// Each statement renders a slider that starts NEUTRAL (centre) with Vrai/Faux
// ends + a hidden `answers[]` checkbox. Clicking a side judges the statement;
// the participant may flip Vrai↔Faux but can NEVER return to neutral once
// moved (that's what enforces "judge every statement"). The slider keeps the
// hidden checkbox in sync (Vrai → checked) so the form posts the selected-set
// with no submit transform. Submit is gated client-side: disabled until no
// slider is still neutral (neutral and Faux both serialise to "unchecked", so
// completeness can only be enforced here).
//
// Element-scoped: binds per-slider once (no document-level listener / seam).

const BOUND_ATTR = 'data-kprime-bound';

export function initKprimeSliders(root = document) {
  const sliders = Array.from(root.querySelectorAll('[data-kprime-slider]'));
  sliders.forEach(bindSlider);

  // Gate every form that owns sliders (covers the all-neutral initial state).
  const forms = new Set();
  sliders.forEach((s) => {
    const form = s.closest('form');
    if (form) forms.add(form);
  });
  forms.forEach(updateGate);
}

function bindSlider(slider) {
  if (!(slider instanceof HTMLElement)) return;
  if (slider.getAttribute(BOUND_ATTR) === '1') return;
  slider.setAttribute(BOUND_ATTR, '1');

  // The flanking Vrai/Faux labels.
  slider.querySelectorAll('[data-kprime-side]').forEach((btn) => {
    btn.addEventListener('click', () => {
      setSide(slider, btn.getAttribute('data-kprime-side'));
    });
  });

  // The track itself: clicking its left half judges Faux, right half Vrai —
  // so the knob moves like a real slider, not only via the end labels.
  const track = slider.querySelector('.kprime-slider__track');
  if (track) {
    track.addEventListener('click', (e) => {
      const rect = track.getBoundingClientRect();
      if (rect.width === 0) return;
      const side = (e.clientX - rect.left) < rect.width / 2 ? 'faux' : 'vrai';
      setSide(slider, side);
    });
  }
}

function setSide(slider, side) {
  if (side !== 'vrai' && side !== 'faux') return;

  // Neutral can only ever be left, never re-entered.
  slider.setAttribute('data-state', side);

  const statement = slider.closest('[data-kprime-statement]');
  const checkbox = statement?.querySelector('[data-kprime-checkbox]');
  if (checkbox instanceof HTMLInputElement) {
    checkbox.checked = (side === 'vrai');
    // The checkbox is hidden + set programmatically, which fires no native
    // event. Notify change listeners explicitly so the quiz-challenge draft
    // autosave (pages/quiz-challenge.js) picks up the judgement. Harmless on
    // the quiz / live-contest submit forms (no change-listener there).
    checkbox.dispatchEvent(new Event('input', { bubbles: true }));
    checkbox.dispatchEvent(new Event('change', { bubbles: true }));
  }

  const form = slider.closest('form');
  if (form) updateGate(form);
}

function updateGate(form) {
  const sliders = Array.from(form.querySelectorAll('[data-kprime-slider]'));
  if (sliders.length === 0) return;

  const allJudged = sliders.every((s) => s.getAttribute('data-state') !== 'neutral');

  const submit = form.querySelector('button[type="submit"], input[type="submit"]');
  if (submit instanceof HTMLButtonElement || submit instanceof HTMLInputElement) {
    submit.disabled = !allJudged;
  }
}
